import { visitForDate, type RouteVisit } from './travellerRoutePolicy.mjs';

/**
 * Removing a destination and everything that belongs only to it — one
 * transaction, shared by the client (utils/destinationRemoval.ts) and the server
 * (changeTripRoute's 'remove'), so both run the same rules. The server adds the
 * branch check (`validate`) the client can't do: it reads branches the caller
 * may not be able to.
 *
 * Never deletes financial records: linked expenses are only marked
 * itineraryDeleted. Never touches another visit's content.
 */
type Body = Record<string, any>;
type Visit = RouteVisit & Body;
export interface RemovalStore {
  get(path: string): Promise<{ exists: boolean; data(): Body | undefined }>;
  set(path: string, data: Body, merge: boolean): void;
  update(path: string, data: Body): void;
  delete(path: string): void;
}
export const SHARED_ITEM_FIELDS = ['attractions', 'eats', 'nightlife', 'shopping', 'notes'] as const;
const dayNumber = (date: string): number | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date ?? '')) return null;
  const parsed = new Date(`${date}T00:00:00Z`);
  return isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date ? null : parsed.getTime() / 86400000;
};

/** Bookings belong to the arriving visit, even when stored on an earlier departure day. */
export function transportDestination<V extends Visit>(destinations: V[], transport: { arr?: string; toCity?: string; to?: string; isRental?: boolean }): V | undefined {
  // A hire can span several visits; removing its return city must not delete it.
  if (transport.isRental) return undefined;
  const owner = visitForDate(destinations, (transport.arr || '').slice(0, 10));
  const city = (name: string) => name.split(',')[0].trim().toLowerCase();
  // An explicit different arrival city (including the journey home) is not owned
  // by this visit. Keep ambiguous legacy bookings rather than guessing destructively.
  const arrivalCity = transport.toCity || transport.to;
  return owner && arrivalCity && city(arrivalCity) === city(owner.name) ? owner : undefined;
}

export async function removeDestination(store: RemovalStore, input: {
  tripId: string; previous: { destinations: Visit[]; startDate?: string; endDate?: string }; destination: Visit;
  dayDates: string[]; scheduleIds: string[]; userId: string; now: string;
  /** Throws to refuse (server: the whole resolved route, branches included). Runs before any write. */
  validate?: (destinations: Visit[]) => void;
}): Promise<{ latest: Body; destinations: Visit[]; accommodations: Body[] }> {
  const { tripId, previous, destination, userId } = input;
  const tripPath = `trips/${tripId}`;
  const tripSnap = await store.get(tripPath);
  if (!tripSnap.exists) throw new Error('This trip is no longer available.');
  const latest = tripSnap.data() as Body;
  const identity = (d: Visit) => JSON.stringify([d.name, d.arrivalDate, d.departureDate]);
  const visits = (c: { destinations: Visit[] }) => (c.destinations || []).map(identity).sort().join('|');
  if (visits(latest as { destinations: Visit[] }) !== visits(previous) || latest.startDate !== previous.startDate || latest.endDate !== previous.endDate) {
    throw new Error('The itinerary changed on another device. Close this panel and review it before deleting.');
  }
  const all = latest.destinations as Visit[];
  const matches = all.filter(d => identity(d) === identity(destination));
  if (matches.length !== 1) throw new Error('This destination changed. Close this panel and review the itinerary before deleting.');
  const removed = matches[0];
  const start = dayNumber(latest.startDate);
  const end = dayNumber(latest.endDate);
  if (start === null || end === null || end < start || end - start > 400) {
    throw new Error('Check the trip dates before deleting this destination. Trips over 400 days need a smaller cleanup.');
  }
  const dates = new Set(input.dayDates);
  for (let day = start; day <= end; day++) dates.add(new Date(day * 86400000).toISOString().slice(0, 10));
  const scheduleIds = new Set([...input.scheduleIds, ...all.map(d => d.arrivalDate)].filter(id => id && id !== '_prefs'));
  if (dates.size + scheduleIds.size > 440) throw new Error('This cleanup is too large to save safely in one operation. Nothing was deleted.');
  const days = await Promise.all([...dates].map(date => store.get(`${tripPath}/itinerary/${date}`)));
  const schedules = await Promise.all([...scheduleIds].map(id => store.get(`${tripPath}/schedules/${id}`)));
  const expenseIds = new Set<string>();
  const removedIds = new Set<string>();
  const retainedIds = new Set<string>();
  const retainedExpenses = new Set<string>();
  const removedItem = (item: { id?: string; expenseLogId?: string; expenseLogIds?: string[] }) => {
    if (item.id) removedIds.add(item.id);
    if (item.expenseLogId) expenseIds.add(item.expenseLogId);
    item.expenseLogIds?.forEach(id => expenseIds.add(id));
  };
  const retainedItem = (item: { id?: string; expenseLogId?: string; expenseLogIds?: string[] }) => {
    if (item.id) retainedIds.add(item.id);
    if (item.expenseLogId) retainedExpenses.add(item.expenseLogId);
    item.expenseLogIds?.forEach(id => retainedExpenses.add(id));
  };
  const owns = (date: string) => visitForDate(all, date) === removed;
  const removeAccommodation = (a: { checkIn?: string; checkOut?: string }, date = '') =>
    owns((a.checkIn || date).slice(0, 10)) && (!a.checkOut || a.checkOut.slice(0, 10) <= removed.departureDate);
  const dayChanges = days.map((snapshot, index) => {
    if (!snapshot.exists) return null;
    const date = [...dates][index];
    const data = snapshot.data() as Body;
    const patch: Body = {};
    const kept = (data.transports || []).filter((t: Body) => {
      if (transportDestination(all, t) !== removed) { retainedItem(t); return true; }
      removedItem(t);
      return false;
    });
    if (kept.length !== (data.transports || []).length) patch.transports = kept;
    if (owns(date)) {
      for (const field of SHARED_ITEM_FIELDS) {
        (data[field] || []).forEach(removedItem);
        patch[field] = [];
      }
      patch.travellers = null;
    } else {
      for (const field of SHARED_ITEM_FIELDS) (data[field] || []).forEach(retainedItem);
    }
    if (data.accommodation) {
      if (removeAccommodation(data.accommodation, date)) { removedItem(data.accommodation); patch.accommodation = null; }
      else retainedItem(data.accommodation);
    }
    if (data.accommodations) {
      const keptAccom = data.accommodations.filter((a: Body) => {
        if (!removeAccommodation(a, date)) { retainedItem(a); return true; }
        removedItem(a); return false;
      });
      if (keptAccom.length !== data.accommodations.length) patch.accommodations = keptAccom;
    }
    return Object.keys(patch).length ? { date, patch } : null;
  });
  const accommodations = ((latest.accommodations || []) as Body[]).filter(a => {
    if (!removeAccommodation(a)) { retainedItem(a); return true; }
    removedItem(a);
    return false;
  });
  retainedIds.forEach(id => removedIds.delete(id));
  retainedExpenses.forEach(id => expenseIds.delete(id));
  if (dates.size + scheduleIds.size + expenseIds.size > 450) throw new Error('This cleanup is too large to save safely in one operation. Nothing was deleted.');
  const expenses = await Promise.all([...expenseIds].map(id => store.get(`${tripPath}/expenses/${id}`)));
  // All reads above; financial amounts, payers, splits and settlements stay intact.
  const destinations = all.filter(d => d !== removed);
  input.validate?.(destinations);
  for (const [index, snapshot] of schedules.entries()) {
    const id = [...scheduleIds][index];
    if (snapshot.exists && id === removed.arrivalDate && destinations.some(d => d.arrivalDate === id)) {
      throw new Error('Two destinations share a schedule. Review their dates before deleting.');
    }
  }
  store.update(tripPath, { destinations, accommodations });
  for (const change of dayChanges) if (change) store.set(`${tripPath}/itinerary/${change.date}`, change.patch, true);
  schedules.forEach((snapshot, index) => {
    const id = [...scheduleIds][index];
    const path = `${tripPath}/schedules/${id}`;
    if (!snapshot.exists) return;
    if (id === removed.arrivalDate) { store.delete(path); return; }
    const schedule = (snapshot.data() as Body).schedule;
    if (!schedule?.days) return;
    const cleaned = schedule.days.filter((day: { date: string }) => !owns(day.date)).map((day: Body) => ({ ...day,
      slots: (day.slots || []).filter((slot: Body) => !removedIds.has(slot.activityId) && !removedIds.has(slot.legAfter?.linkedId)),
    }));
    if (JSON.stringify(cleaned) !== JSON.stringify(schedule.days)) store.update(path, { schedule: { ...schedule, days: cleaned } });
  });
  expenses.forEach((snapshot, index) => {
    if (snapshot.exists) store.update(`${tripPath}/expenses/${[...expenseIds][index]}`, {
      itineraryDeleted: true, itineraryDeletedBy: userId, itineraryDeletedAt: input.now,
    });
  });
  return { latest, destinations, accommodations };
}
